/**
 * The Rules engine's shared vocabulary, mirrored from the backend
 * (`kim-ai-backend/src/types/ticketRules.types.ts`). Same names and shapes on purpose: a change
 * there is a contract change here, see the backend's docs/rules-engine.md.
 */

/**
 * "After" triggers run once something has already happened (Kim replied, a code was used, a comment
 * was hidden). They react; they never decide, reply or hold anything.
 */
export const AFTER_TRIGGERS = [
  'after_reply',
  'after_draft',
  'after_handoff',
  'after_tool',
  'after_close',
  'after_discount_sent',
  'after_discount_used',
  'after_products_recommended',
  'after_comment_action',
] as const;
export type AfterTrigger = (typeof AFTER_TRIGGERS)[number];

export const RULE_TRIGGERS = ['message_received', 'social_comment', 'ai_answer', ...AFTER_TRIGGERS] as const;
export type RuleTrigger = (typeof RULE_TRIGGERS)[number];

export const isAfterTrigger = (trigger: string): trigger is AfterTrigger =>
  (AFTER_TRIGGERS as readonly string[]).includes(trigger);

export const RULE_STATUSES = ['draft', 'shadow', 'live', 'paused'] as const;
export type RuleStatus = (typeof RULE_STATUSES)[number];

export const RULE_OUTCOMES = ['hand_to_kim', 'hand_to_team', 'stop'] as const;
export type RuleOutcome = (typeof RULE_OUTCOMES)[number];

/** What an "after" event carries; each event lists the ones it fills in. */
export const EVENT_FACT_IDS = [
  'reply.text',
  'reply.by',
  'reply.confidence',
  'reply.intent',
  'draft.reason',
  'handoff.action',
  'handoff.result',
  'handoff.reason',
  'tool.name',
  'tool.result',
  'tool.detail',
  'tool.by',
  'close.by',
  'close.intent',
  'discount.code',
  'products.names',
  'products.count',
  'comment.text',
  'comment.action',
  'comment.reply',
  'event.check',
] as const;
export type EventFactId = (typeof EVENT_FACT_IDS)[number];

export const FACT_IDS = [
  'message.text',
  'message.small_talk',
  'message.word_count',
  'message.has_attachment',
  'conversation.customer_messages',
  'conversation.is_first_message',
  'channel',
  'helpdesk',
  'inbox',
  'customer.email',
  'customer.email_domain',
  'tool.value',
  'ticket.tags',
  'ticket.assigned_to_human',
  'time.business_hours',
  'time.kim_hours',
  'ai.intent',
  'ai.buying_intent',
  'ai.sentiment',
  'ai.check',
  'cart.total',
  'cart.items',
  'cart.has_discount_code',
  'social.platform',
  'social.kind',
  'answer.text',
  'answer.confidence',
  'answer.intent',
  'answer.will_send',
  'answer.held_back',
  'answer.needs_approval',
  'answer.has_actions',
  'answer.check',
  ...EVENT_FACT_IDS,
] as const;
export type FactId = (typeof FACT_IDS)[number];

export const OPERATOR_IDS = [
  'is',
  'is_not',
  'contains_any',
  'contains_none',
  'starts_with',
  'matches_pattern',
  'is_any_of',
  'is_none_of',
  'has_other_than',
  'gt',
  'gte',
  'lt',
  'lte',
  'is_true',
  'is_false',
  'is_empty',
  'is_not_empty',
] as const;
export type OperatorId = (typeof OPERATOR_IDS)[number];

export const ACTION_IDS = [
  'reply',
  'add_tags',
  'remove_tags',
  'add_note',
  'assign',
  'close',
  'sales_discount',
  'guide_ai',
  'acknowledge',
  'hold_message',
  'delay_reply',
  'comment_reply',
  'comment_like',
  'comment_hide',
  'comment_delete',
  'social_agent',
  'notify_slack',
  'send_email',
  'call_webhook',
  'run_tool',
] as const;
export type ActionId = (typeof ACTION_IDS)[number];

/** Where "A value from a connected tool" reads from: a lookup tool, its input, and a field of its answer. */
export interface ToolValueSource {
  provider: string;
  tool: string;
  /** `{{customer.email}}` and `{{ticket.id}}` are filled in from the message. Text that didn't parse stays text, and saving refuses it. */
  input?: Record<string, unknown> | string;
  /** Dot path into the answer, e.g. `customer.total_spent`. */
  path: string;
}

export interface RuleCondition {
  id: string;
  fact: FactId;
  op: OperatorId;
  /** string | number | boolean | string[] depending on the fact's value input; absent for unary operators. */
  value?: unknown;
  /** Only for `tool.value`. */
  source?: ToolValueSource;
}

export interface IfStep {
  id: string;
  kind: 'if';
  match: 'all' | 'any';
  conditions: RuleCondition[];
  then: RuleStep[];
  else: RuleStep[];
}

export interface ActionStep {
  id: string;
  kind: 'action';
  action: ActionId;
  params: Record<string, unknown>;
}

export interface OutcomeStep {
  id: string;
  kind: 'outcome';
  outcome: RuleOutcome;
}

export type RuleStep = IfStep | ActionStep | OutcomeStep;

/** The editable part of a rule — what the builder sends and what the engine walks. */
export interface RuleDefinition {
  name: string;
  description?: string;
  trigger: RuleTrigger;
  steps: RuleStep[];
}

/** A stored rule, as the API returns it. */
export interface RuleRecord extends RuleDefinition {
  id: string;
  accountId: number;
  buId: number | null;
  status: RuleStatus;
  priority: number;
  origin: 'brand' | 'system';
  systemKey?: string | null;
  templateId?: string | null;
  version: number;
  createdBy?: string | null;
  updatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Runs in the last 7 days where this rule matched (list endpoint only). */
  matchedLast7Days?: number;
}

// Catalog: everything the builder renders, served by GET /api/ticket-rules/catalog

/**
 * How the builder asks for a condition's value. `intents`, `tags` and `emails` use the app's existing
 * pickers; `enum` uses `options`.
 */
export type ValueInput = 'none' | 'text' | 'texts' | 'number' | 'enum' | 'enums' | 'intents' | 'tags' | 'emails';

export interface CatalogOption {
  value: string;
  label: string;
}

export interface CatalogOperator {
  id: OperatorId;
  /** Reads after the fact label: "The message" + "contains any of". */
  label: string;
  /** How the value is entered for this operator on this fact (overrides nothing — set per fact). */
  needsValue: boolean;
}

export interface CatalogFact {
  id: FactId;
  label: string;
  group: 'Message' | 'Customer' | 'Ticket' | 'Time' | 'AI' | 'Sales' | 'Social' | 'Advanced AI answer' | 'What happened';
  /** One-line hint shown under the picker. */
  help: string;
  operators: OperatorId[];
  /** Value input per operator; operators missing here use `valueInput`. */
  valueInput: ValueInput;
  valueInputByOperator?: Partial<Record<OperatorId, ValueInput>>;
  options?: CatalogOption[];
  triggers: RuleTrigger[];
  /** `free` facts cost nothing; `lookup` reads the helpdesk; `ai` makes a small model call. */
  cost: 'free' | 'lookup' | 'ai';
}

export interface CatalogParam {
  key: string;
  label: string;
  input: 'text' | 'textarea' | 'tags' | 'enum' | 'boolean' | 'email' | 'number';
  /** For `number`: the allowed range, inclusive. */
  min?: number;
  max?: number;
  options?: CatalogOption[];
  required?: boolean;
  placeholder?: string;
  help?: string;
  /** Only shown when another param has this value, e.g. `instructions` when `mode` = `ai`. */
  showWhen?: { key: string; equals: string | boolean };
  default?: string | boolean | string[] | number;
}

export interface CatalogAction {
  id: ActionId;
  label: string;
  /** Sentence used in summaries: "reply to the customer". */
  verb: string;
  help: string;
  params: CatalogParam[];
  triggers: RuleTrigger[];
  /** Writes to the helpdesk (shown with a "writes" hint; never runs in shadow). */
  writes: boolean;
}

export interface CatalogOutcome {
  id: RuleOutcome;
  label: string;
  help: string;
  /** Where the decision means something else, its words for that trigger; null where it isn't offered. */
  byTrigger?: Partial<Record<RuleTrigger, { label: string; help: string } | null>>;
}

export interface CatalogTrigger {
  id: RuleTrigger;
  label: string;
  help: string;
  /** `before`: Kim hasn't acted yet, so a playbook can decide. `after`: it already happened; actions only. */
  phase: 'before' | 'after';
}

export interface RuleTemplate {
  id: string;
  name: string;
  description: string;
  /** Short category for the gallery filter. */
  category: 'Speed' | 'Routing' | 'Filtering' | 'Tagging' | 'Sales' | 'Social' | 'Quality' | 'Alerts';
  definition: RuleDefinition;
}

export interface RulesCatalog {
  triggers: CatalogTrigger[];
  facts: CatalogFact[];
  operators: CatalogOperator[];
  actions: CatalogAction[];
  outcomes: CatalogOutcome[];
  templates: RuleTemplate[];
}

// Runs

export type StepResult = 'true' | 'false' | 'done' | 'skipped' | 'would_do' | 'failed';

export interface StepTrace {
  stepId: string;
  kind: RuleStep['kind'];
  result: StepResult;
  /** Human-readable reason, e.g. "intent was order_status" or "Shadow Mode is on". */
  detail?: string;
}

export interface RuleTrace {
  ruleId: string;
  name: string;
  version: number;
  status: RuleStatus;
  /** True when the rule reached at least one action or outcome. */
  matched: boolean;
  outcome?: RuleOutcome;
  steps: StepTrace[];
}

export interface ActionRecord {
  ruleId: string;
  stepId: string;
  action: ActionId;
  status: 'done' | 'skipped' | 'failed' | 'would_do';
  detail?: string;
  ms?: number;
}

export interface ReplyRecord {
  text: string;
  mode: 'template' | 'ai';
  sent: boolean;
  /** Why it wasn't sent, or that the AI fell back to the template. */
  reason?: string;
  ms: number;
}

export type RunStatus = 'completed' | 'failed' | 'fallback';

export interface RunRecord {
  id: string;
  accountId: number;
  buId: number | null;
  trigger: RuleTrigger;
  provider: string;
  ticketId: string;
  messageId?: number | null;
  channel: string;
  status: RunStatus;
  outcome: RuleOutcome;
  decidedBy?: { ruleId: string; ruleName: string; version: number } | null;
  rules: RuleTrace[];
  actions: ActionRecord[];
  reply?: ReplyRecord | null;
  facts: Record<string, unknown>;
  timings: { totalMs: number; factsMs: number; actionsMs: number };
  error?: string | null;
  /** True for a dry run from the builder's Test panel (never stored). */
  dryRun?: boolean;
  createdAt: string;
}

/** Body of POST /api/ticket-rules/test. */
export interface TestRequest {
  message: string;
  channel?: string;
  customerEmail?: string;
  tags?: string[];
  isFirstMessage?: boolean;
  trigger?: RuleTrigger;
  /** For "Advanced AI answers": `message` is the reply, and these describe the rest of the answer. */
  answer?: {
    confidence?: number | null;
    intents?: string[];
    willSend?: boolean;
    heldBack?: boolean;
    needsApproval?: boolean;
    hasActions?: boolean;
  };
  /** For an "after" trigger: what happened, as the event would carry it (`reply.text`, `tool.name`, …). */
  event?: Partial<Record<EventFactId, unknown>>;
  /** A pretend shopper's cart, for Sales playbooks. */
  cart?: { total?: number | null; items?: number | null; hasDiscountCode?: boolean | null };
  /** Unsaved rules to test instead of the brand's saved ones (the builder sends its draft). */
  rules?: Array<RuleDefinition & { id?: string; status?: RuleStatus }>;
}

/** One move onto playbooks or back, newest first. */
export interface MigrationEntry {
  event: 'migrated' | 'rolled_back';
  actorEmail: string | null;
  conversion: { created: string[]; kept: string[]; paused: string[]; failed: string[] } | null;
  at: string;
}

/** GET /api/ticket-rules/migration: whether playbooks are on, what migrating would create, and the history. */
export interface MigrationStatus {
  enabled: boolean;
  /** Playbooks are on and the brand was migrated, so they handle the brand's old settings. */
  ownsSettings: boolean;
  preview: Array<{ systemKey: string; name: string; description: string; trigger: RuleTrigger }>;
  history: MigrationEntry[];
}

/** Body of POST /api/ticket-rules: from a template, or a whole rule. */
export type CreateRuleRequest = { templateId: string } | (RuleDefinition & { status?: RuleStatus });

/** Body of PUT /api/ticket-rules/:id. */
export type UpdateRuleRequest = RuleDefinition & { status?: RuleStatus };

/** Filters of GET /api/ticket-rules/runs. */
export interface RunsFilter {
  ruleId?: string;
  outcome?: RuleOutcome;
  status?: RunStatus;
  ticketId?: string;
}

/** One page of GET /api/ticket-rules/runs, newest first. */
export interface RunsPage {
  items: RunRecord[];
  nextCursor: string | null;
}
